import type {AuditProcedure} from "../../types";

// --- Audit Procedures ---

export const AUDIT_PROCEDURES: AuditProcedure[] = [
  // ── Expenditures / Purchase transactions ──
  {
    id: "ap-001",
    procedureNumber: "AP-EXP-01",
    title: "Test segregation of duties in purchase requisition approvals",
    description:
      "Select a sample of purchase requisitions and verify that the preparer and approver are different individuals with appropriate authority levels.",
    methodology: "test-of-controls",
    techniques: ["inspection", "reperformance"],
    timing: "interim",
    extent: "Sample of 25",
    assignedTo: "Sarah Chen",
    status: "in-progress",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-transactions",
    assertionIds: ["existence", "accuracy"],
    riskIds: ["risk-exp-001"],
    controlIds: ["ctrl-exp-001"],
    processId: "proc-vendor-invoice",
    elcId: null,
  },
  {
    id: "ap-002",
    procedureNumber: "AP-EXP-02",
    title: "Reperform three-way match for sample of invoices",
    description:
      "For a sample of vendor invoices, independently reperform the three-way match (PO, goods receipt, invoice) and verify amounts agree within tolerance.",
    methodology: "substantive-detail",
    techniques: ["reperformance", "inspection"],
    timing: "year-end",
    extent: "Sample of 30",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-transactions",
    assertionIds: ["accuracy", "completeness"],
    riskIds: ["risk-exp-002"],
    controlIds: ["ctrl-exp-002"],
    processId: "proc-vendor-invoice",
    elcId: null,
  },
  {
    id: "ap-003",
    procedureNumber: "AP-EXP-03",
    title: "Vouching of expense reimbursements to supporting documentation",
    description:
      "Select a sample of employee expense reimbursements and trace to original receipts, manager approvals, and policy compliance documentation.",
    methodology: "substantive-detail",
    techniques: ["inspection"],
    timing: "both",
    extent: "Sample of 20",
    assignedTo: "James Wilson",
    status: "in-progress",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-transactions",
    assertionIds: ["existence", "accuracy"],
    riskIds: ["risk-exp-003"],
    controlIds: ["ctrl-exp-003"],
    processId: "proc-expense-reimbursement",
    elcId: null,
  },
  {
    id: "ap-004",
    procedureNumber: "AP-EXP-04",
    title: "Analytical review of expense trends by category",
    description:
      "Perform analytical procedures comparing current-year expense categories to prior year and budget, investigating variances exceeding 10%.",
    methodology: "substantive-analytical",
    techniques: ["inspection"],
    timing: "year-end",
    extent: "All expense categories",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-transactions",
    assertionIds: ["completeness", "accuracy"],
    riskIds: [],
    controlIds: [],
    processId: "proc-expense-reimbursement",
    elcId: null,
  },
  {
    id: "ap-005",
    procedureNumber: "AP-EXP-05",
    title: "Test corporate card reconciliation controls",
    description:
      "For a sample of months, inspect completed corporate card reconciliations and verify they were prepared and reviewed by appropriate personnel within the required timeframe.",
    methodology: "test-of-controls",
    techniques: ["observation", "inquiry"],
    timing: "interim",
    extent: "3 months selected",
    assignedTo: "Sarah Chen",
    status: "in-review",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-transactions",
    assertionIds: ["completeness", "accuracy"],
    riskIds: ["risk-exp-004"],
    controlIds: ["ctrl-exp-004"],
    processId: "proc-corp-card",
    elcId: null,
  },
  // ── Expenditures / AP balance ──
  {
    id: "ap-006",
    procedureNumber: "AP-EXP-06",
    title: "Accounts payable confirmation and reconciliation",
    description:
      "Confirm a sample of AP balances with vendors and reconcile the AP sub-ledger to the general ledger at year-end.",
    methodology: "substantive-detail",
    techniques: ["confirmation", "inspection"],
    timing: "year-end",
    extent: "Top 15 vendors by balance",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-balance",
    assertionIds: ["existence", "completeness", "valuation"],
    riskIds: ["risk-exp-005"],
    controlIds: [],
    processId: "proc-vendor-invoice",
    elcId: null,
  },
  {
    id: "ap-007",
    procedureNumber: "AP-EXP-07",
    title: "Search for unrecorded liabilities",
    description:
      "Examine payments made after year-end and review open purchase orders to identify potential unrecorded liabilities at the balance sheet date.",
    methodology: "substantive-detail",
    techniques: ["inspection"],
    timing: "year-end",
    extent: "All payments > $50k in Jan",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-balance",
    assertionIds: ["completeness", "cutoff"],
    riskIds: ["risk-exp-006"],
    controlIds: [],
    processId: "proc-vendor-invoice",
    elcId: null,
  },
  // ── Revenue / Revenue transactions ──
  {
    id: "ap-008",
    procedureNumber: "AP-REV-01",
    title: "Test revenue cut-off at period end",
    description:
      "Select sales transactions around year-end and verify revenue was recognised in the correct period by examining shipping documents and delivery confirmation.",
    methodology: "substantive-detail",
    techniques: ["inspection"],
    timing: "year-end",
    extent: "Sample of 25 (5 days either side)",
    assignedTo: "Emily Park",
    status: "in-progress",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-transactions",
    assertionIds: ["cutoff", "existence"],
    riskIds: ["risk-rev-001"],
    controlIds: ["ctrl-rev-001"],
    processId: "proc-order-fulfillment",
    elcId: null,
  },
  {
    id: "ap-009",
    procedureNumber: "AP-REV-02",
    title: "Evaluate contract revenue recognition judgements",
    description:
      "For a sample of multi-element contracts, evaluate management's identification of performance obligations and allocation of transaction price under ASC 606.",
    methodology: "substantive-detail",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "Sample of 10 contracts",
    assignedTo: "Emily Park",
    status: "not-ready",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-transactions",
    assertionIds: ["accuracy", "existence"],
    riskIds: ["risk-rev-002"],
    controlIds: ["ctrl-rev-002"],
    processId: "proc-contract-rev-rec",
    elcId: null,
  },
  {
    id: "ap-010",
    procedureNumber: "AP-REV-03",
    title: "Test automated billing controls",
    description:
      "Test the automated controls in the billing system that generate invoices upon shipment confirmation and verify pricing against master data.",
    methodology: "test-of-controls",
    techniques: ["inspection", "reperformance"],
    timing: "interim",
    extent: "Sample of 25 invoices",
    assignedTo: "Emily Park",
    status: "in-review",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-transactions",
    assertionIds: ["accuracy", "completeness"],
    riskIds: ["risk-rev-003"],
    controlIds: ["ctrl-rev-003"],
    processId: "proc-order-fulfillment",
    elcId: null,
  },
  {
    id: "ap-011",
    procedureNumber: "AP-REV-04",
    title: "Revenue analytical procedures",
    description:
      "Perform analytical procedures on revenue by product line, geography, and customer segment, comparing to prior year and investigating significant fluctuations.",
    methodology: "substantive-analytical",
    techniques: ["inspection"],
    timing: "year-end",
    extent: "All revenue streams",
    assignedTo: "Emily Park",
    status: "not-ready",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-transactions",
    assertionIds: ["completeness", "accuracy"],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  // ── Revenue / AR balance ──
  {
    id: "ap-012",
    procedureNumber: "AP-REV-05",
    title: "Accounts receivable confirmations",
    description:
      "Send positive confirmations to a sample of customers and perform alternative procedures for non-responses.",
    methodology: "substantive-detail",
    techniques: ["confirmation"],
    timing: "year-end",
    extent: "Top 20 customers",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-balance",
    assertionIds: ["existence", "valuation"],
    riskIds: ["risk-rev-004"],
    controlIds: [],
    processId: "proc-credit-collections",
    elcId: null,
  },
  {
    id: "ap-013",
    procedureNumber: "AP-REV-06",
    title: "Test allowance for doubtful accounts estimate",
    description:
      "Evaluate the reasonableness of the bad debt allowance by reviewing aging analysis, historical write-off rates, and specific customer assessments.",
    methodology: "substantive-detail",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "Full balance review",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-balance",
    assertionIds: ["valuation"],
    riskIds: ["risk-rev-005"],
    controlIds: ["ctrl-rev-005"],
    processId: "proc-credit-collections",
    elcId: null,
  },
  // ── Financial Reporting / Journal entries ──
  {
    id: "ap-014",
    procedureNumber: "AP-FR-01",
    title: "Test journal entry controls (dual approval)",
    description:
      "For a sample of manual journal entries, verify that dual approval was obtained and supporting documentation is adequate.",
    methodology: "test-of-controls",
    techniques: ["inspection"],
    timing: "both",
    extent: "Sample of 25",
    assignedTo: "Sarah Chen",
    status: "in-progress",
    fsaId: "financial-reporting",
    cotabdId: "cotabd-fr-transactions",
    assertionIds: ["existence", "accuracy"],
    riskIds: ["risk-fr-001"],
    controlIds: ["ctrl-fr-001"],
    processId: "proc-journal-entry",
    elcId: null,
  },
  {
    id: "ap-015",
    procedureNumber: "AP-FR-02",
    title: "Risk-based journal entry testing (fraud focus)",
    description:
      "Using data analytics, identify and test journal entries with characteristics indicative of potential management override (e.g., unusual amounts, off-hours postings, round numbers).",
    methodology: "substantive-detail",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "All entries meeting criteria",
    assignedTo: "Emily Park",
    status: "not-ready",
    fsaId: "financial-reporting",
    cotabdId: "cotabd-fr-transactions",
    assertionIds: ["existence", "accuracy"],
    riskIds: ["risk-fr-002"],
    controlIds: [],
    processId: "proc-journal-entry",
    elcId: null,
  },
  // ── Financial Reporting / GL balances ──
  {
    id: "ap-016",
    procedureNumber: "AP-FR-03",
    title: "Month-end close reconciliation review",
    description:
      "Review a sample of balance sheet reconciliations prepared during the close process, verifying completeness, accuracy, and timely review.",
    methodology: "test-of-controls",
    techniques: ["inspection"],
    timing: "interim",
    extent: "3 months, all material accounts",
    assignedTo: "Sarah Chen",
    status: "in-review",
    fsaId: "financial-reporting",
    cotabdId: "cotabd-fr-balance",
    assertionIds: ["completeness", "valuation"],
    riskIds: ["risk-fr-003"],
    controlIds: ["ctrl-fr-003"],
    processId: "proc-month-end-close",
    elcId: null,
  },
  {
    id: "ap-017",
    procedureNumber: "AP-FR-04",
    title: "Test intercompany elimination entries",
    description:
      "Verify that intercompany transactions have been properly identified and eliminated in the consolidation, with no residual balances.",
    methodology: "substantive-detail",
    techniques: ["reperformance", "inspection"],
    timing: "year-end",
    extent: "All intercompany pairs",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: "financial-reporting",
    cotabdId: "cotabd-fr-balance",
    assertionIds: ["completeness", "accuracy"],
    riskIds: ["risk-fr-004"],
    controlIds: ["ctrl-fr-004"],
    processId: "proc-intercompany",
    elcId: null,
  },
  // ── Expenditure disclosure ──
  {
    id: "ap-018",
    procedureNumber: "AP-EXP-08",
    title: "Review expenditure note disclosures for completeness",
    description:
      "Inspect financial statement disclosures related to expenditures and verify they meet applicable framework requirements.",
    methodology: "substantive-detail",
    techniques: ["inspection"],
    timing: "year-end",
    extent: "All expenditure disclosures",
    assignedTo: "Emily Park",
    status: "not-ready",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-disclosure",
    assertionIds: ["completeness", "accuracy", "presentation"],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  // ── Revenue disclosure ──
  {
    id: "ap-019",
    procedureNumber: "AP-REV-07",
    title: "Review revenue recognition policy disclosures",
    description:
      "Verify that revenue recognition policy disclosures are complete, accurate, and consistent with the entity's actual practices under ASC 606.",
    methodology: "substantive-detail",
    techniques: ["inspection"],
    timing: "year-end",
    extent: "All revenue disclosures",
    assignedTo: "Emily Park",
    status: "not-ready",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-disclosure",
    assertionIds: ["completeness", "accuracy", "presentation"],
    riskIds: [],
    controlIds: [],
    processId: "proc-contract-rev-rec",
    elcId: null,
  },
  // ── ELC Procedures ──
  {
    id: "ap-020",
    procedureNumber: "AP-ELC-01",
    title: "Test governance oversight through board minutes inspection",
    description:
      "For board and Audit Committee meetings held during the period, inspect minutes to verify that financial reporting matters were actively discussed, challenged, and resolved in a timely manner.",
    methodology: "test-of-controls",
    techniques: ["inspection"],
    timing: "both",
    extent: "All board/AC meetings in period",
    assignedTo: "Sarah Chen",
    status: "in-progress",
    fsaId: null,
    cotabdId: null,
    assertionIds: [],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: "elc-control-environment",
  },
  {
    id: "ap-021",
    procedureNumber: "AP-ELC-02",
    title: "Test ongoing competence and training controls for finance personnel",
    description:
      "For key finance personnel, verify that required training was completed during the period, certifications remained current, and performance reviews addressing financial reporting competence were conducted as scheduled.",
    methodology: "test-of-controls",
    techniques: ["inquiry", "inspection"],
    timing: "interim",
    extent: "Key finance team (8 individuals)",
    assignedTo: "James Wilson",
    status: "in-review",
    fsaId: null,
    cotabdId: null,
    assertionIds: [],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: "elc-control-environment",
  },
  {
    id: "ap-022",
    procedureNumber: "AP-ELC-03",
    title: "Test IT change management controls for GITCs",
    description:
      "For a sample of application changes deployed during the period, verify that each change followed the required approval workflow, maintained segregation between development and production, and was tested prior to deployment.",
    methodology: "test-of-controls",
    techniques: ["observation", "inquiry"],
    timing: "interim",
    extent: "3 application changes",
    assignedTo: "Sarah Chen",
    status: "in-progress",
    fsaId: null,
    cotabdId: null,
    assertionIds: [],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: "elc-it-environment",
  },
  {
    id: "ap-023",
    procedureNumber: "AP-ELC-04",
    title: "Test logical access controls for key financial systems",
    description:
      "For the ERP and billing systems, select a sample of new starters, role changes, and terminations during the period and verify that access was provisioned, modified, or revoked in accordance with policy and within required timeframes.",
    methodology: "test-of-controls",
    techniques: ["inspection"],
    timing: "both",
    extent: "All users with privileged access",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: [],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: "elc-it-environment",
  },
  {
    id: "ap-024",
    procedureNumber: "AP-ELC-05",
    title: "Test monitoring activities and internal audit function",
    description:
      "Inspect internal audit reports and monitoring logs produced during the period to verify that planned monitoring activities were executed on schedule, findings were reported to appropriate levels, and remediation actions were tracked to completion.",
    methodology: "test-of-controls",
    techniques: ["inquiry", "inspection"],
    timing: "interim",
    extent: "IA reports and monitoring logs",
    assignedTo: "Emily Park",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: [],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: "elc-monitoring-governance",
  },
  {
    id: "ap-025",
    procedureNumber: "AP-ELC-06",
    title: "Test entity's risk assessment process",
    description:
      "Verify that management's risk assessment was updated during the period in response to changes in the business environment, that newly identified risks were escalated and addressed, and that fraud risk assessments were performed as required.",
    methodology: "test-of-controls",
    techniques: ["inquiry"],
    timing: "interim",
    extent: "Management interviews + documentation",
    assignedTo: "Emily Park",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: [],
    riskIds: [],
    controlIds: [],
    processId: null,
    elcId: "elc-risk-assessment",
  },
  // ── Additional process-level procedures ──
  {
    id: "ap-026",
    procedureNumber: "AP-EXP-09",
    title: "Test vendor master data change controls",
    description:
      "For a sample of vendor master data changes during the period, verify that each change was initiated by an authorised requester, independently approved, and supported by appropriate documentation.",
    methodology: "test-of-controls",
    techniques: ["inquiry"],
    timing: "interim",
    extent: "Key personnel interviews",
    assignedTo: "James Wilson",
    status: "in-progress",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-transactions",
    assertionIds: ["existence"],
    riskIds: ["risk-exp-007"],
    controlIds: [],
    processId: "proc-vendor-invoice",
    elcId: null,
  },
  {
    id: "ap-027",
    procedureNumber: "AP-FR-05",
    title: "Observe month-end close meeting and checklist completion",
    description:
      "Attend a month-end close meeting to observe the process, review the close checklist, and assess timeliness and completeness of close activities.",
    methodology: "test-of-controls",
    techniques: ["observation", "inspection"],
    timing: "interim",
    extent: "1 month-end observation",
    assignedTo: "Sarah Chen",
    status: "in-review",
    fsaId: "financial-reporting",
    cotabdId: "cotabd-fr-balance",
    assertionIds: ["completeness"],
    riskIds: ["risk-fr-003"],
    controlIds: ["ctrl-fr-003"],
    processId: "proc-month-end-close",
    elcId: null,
  },
  {
    id: "ap-028",
    procedureNumber: "AP-REV-08",
    title: "Observe credit approval process for new customers",
    description:
      "Observe the credit approval process for a sample of new customer accounts, verifying that credit checks and limit-setting procedures are followed.",
    methodology: "test-of-controls",
    techniques: ["observation"],
    timing: "interim",
    extent: "5 new customer approvals",
    assignedTo: "Emily Park",
    status: "in-progress",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-balance",
    assertionIds: ["existence", "valuation"],
    riskIds: ["risk-rev-004"],
    controlIds: [],
    processId: "proc-credit-collections",
    elcId: null,
  },

  // ── Fraud & Management Override ──
  {
    id: "ap-frd-01",
    procedureNumber: "AP-FRD-01",
    title: "Full-population journal entry data analytics",
    description:
      "Run data analytics over the complete journal entry population for the year, applying risk criteria (off-hours, unusual posters, revenue/COGS impact, round numbers >£50K, period-end clustering).",
    methodology: "substantive-detail",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "Full population",
    assignedTo: "Priya Patel",
    status: "in-progress",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["existence", "accuracy", "completeness"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-02",
    procedureNumber: "AP-FRD-02",
    title: "Test journal entries posted by dual-access users",
    description:
      "For the three users with dual prepare/post access, select all journal entries above materiality and test for appropriate business rationale and supporting documentation.",
    methodology: "substantive-detail",
    techniques: ["inspection", "inquiry"],
    timing: "year-end",
    extent: "All entries by dual-access users >£225K",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["existence", "accuracy"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-03",
    procedureNumber: "AP-FRD-03",
    title: "Retrospective review of accounting estimates",
    description:
      "Perform retrospective analysis of prior-year estimates (inventory obsolescence, warranty provision, revenue contract modifications) comparing estimates to actual outcomes.",
    methodology: "substantive-analytical",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "All significant estimates",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["valuation", "accuracy"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-04",
    procedureNumber: "AP-FRD-04",
    title: "Evaluate range of reasonable outcomes for key estimates",
    description:
      "For each significant estimate, independently develop the range of reasonable outcomes and assess whether management's point estimate consistently falls at the favourable end.",
    methodology: "substantive-analytical",
    techniques: ["reperformance"],
    timing: "year-end",
    extent: "3 key estimates",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["valuation"],
    riskIds: ["fraud-risk-02"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-05",
    procedureNumber: "AP-FRD-05",
    title: "Evaluate significant unusual transactions",
    description:
      "Identify and evaluate all significant unusual transactions including year-end adjustments, related party transactions with Westfield Components, and Q4 asset disposals.",
    methodology: "substantive-detail",
    techniques: ["inspection", "inquiry"],
    timing: "year-end",
    extent: "All identified transactions",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: null,
    cotabdId: null,
    assertionIds: ["existence", "accuracy", "completeness", "presentation"],
    riskIds: ["fraud-risk-05"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-06",
    procedureNumber: "AP-FRD-06",
    title: "Revenue recognition fraud testing — risk-stratified contract sample",
    description:
      "Select a risk-stratified sample of multi-element contracts and independently assess revenue recognition: performance obligation identification, SSP allocation, and timing.",
    methodology: "substantive-detail",
    techniques: ["inspection", "reperformance"],
    timing: "year-end",
    extent: "Sample of 15 contracts",
    assignedTo: "Sarah Chen",
    status: "not-ready",
    fsaId: "revenue",
    cotabdId: "cotabd-rev-transactions",
    assertionIds: ["existence", "accuracy", "cutoff"],
    riskIds: ["fraud-risk-01"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
  {
    id: "ap-frd-07",
    procedureNumber: "AP-FRD-07",
    title: "Inventory existence testing at external warehouses",
    description:
      "Attend inventory counts at all four external warehouse locations. Perform independent test counts and reconcile to the perpetual inventory system.",
    methodology: "substantive-detail",
    techniques: ["observation", "inspection", "reperformance"],
    timing: "year-end",
    extent: "All 4 warehouses",
    assignedTo: "James Wilson",
    status: "not-ready",
    fsaId: "expenditures",
    cotabdId: "cotabd-exp-balances",
    assertionIds: ["existence", "valuation"],
    riskIds: ["fraud-risk-03"],
    controlIds: [],
    processId: null,
    elcId: null,
  },
];

// --- Narratives ---

export const AUDIT_PROGRAMME_FSA_NARRATIVES: Record<string, string> = {
  expenditures: `## Expenditures — Audit Strategy

Our audit strategy for expenditures combines a **controls-reliance approach** for high-volume transaction processing with targeted substantive procedures at year-end.

**Interim phase:** We will test the operating effectiveness of key controls over the procure-to-pay cycle, including segregation of duties in purchase requisition approvals, three-way matching, and corporate card reconciliation. These tests of controls will be performed at the interim date and updated through roll-forward procedures.

**Year-end phase:** Substantive procedures will focus on higher-risk areas including unrecorded liabilities, vendor confirmations for material balances, and analytical review of expense trends by category. The search for unrecorded liabilities will cover all payments exceeding $50,000 made in January following year-end.

**Staffing:** Primary procedures assigned to Sarah Chen (senior) and James Wilson (staff), with partner review of all significant judgements.`,

  revenue: `## Revenue — Audit Strategy

Revenue is assessed as a **significant risk** given the complexity of multi-element arrangements under ASC 606 and the inherent judgement in determining performance obligations and transaction price allocation.

**Combined approach:** We will place limited reliance on automated billing controls (tested at interim) and supplement with extensive substantive procedures. Revenue cut-off testing will be performed using a sample of transactions from five days either side of year-end.

**Key focus areas:**
- Contract revenue recognition judgements for multi-element arrangements
- Revenue analytical procedures by product line, geography, and customer segment
- Accounts receivable confirmations and assessment of the bad debt allowance

**Staffing:** Emily Park (senior) will lead revenue testing, with support from James Wilson on receivables.`,

  "financial-reporting": `## Financial Reporting — Audit Strategy

The financial reporting close process is critical to the integrity of the overall financial statements. Our strategy emphasises a **dual approach** combining controls testing with substantive procedures.

**Controls reliance:** We will test the dual-approval control over journal entries and the month-end reconciliation review process at interim, with roll-forward procedures through year-end.

**Substantive focus:** Risk-based journal entry testing using data analytics will target entries with characteristics indicative of management override. Intercompany elimination testing will cover all entity pairs to verify no residual balances remain.

**Observation:** We will attend a month-end close meeting to directly observe the process and assess the control environment in practice.`,
};
